import type { IndustryPage } from '../industries';

const lines = [
  { item: 'House soft wash, two-storey vinyl, about 2,400 sq ft of siding, all four sides', amount: '$450' },
  { item: 'Driveway and front walk, surface cleaner and post-treat, about 1,100 sq ft', amount: '$180' },
  { item: 'Back patio pavers, clean and re-sand joints, about 400 sq ft', amount: '$220' },
  { item: 'Optional: gutter face brightening on the front elevation', amount: '$90' },
];

const faq = [
  {
    q: 'How do pressure washing companies price a house wash?',
    a: 'Most price siding by square foot or by house size and storeys, and flatwork like driveways and patios by square foot. Oxidation, mildew on the north side, height and access all move the number. Showing each surface as its own line keeps the comparison with other bids honest.',
  },
  {
    q: 'Should I explain soft washing versus pressure washing on the quote?',
    a: 'Yes. Homeowners often think high pressure is the whole job and worry about damage. One line on the quote saying which surfaces get low-pressure soft washing with a cleaning solution, and which get the surface cleaner, answers that before you arrive.',
  },
  {
    q: 'Can a homeowner add the patio or the gutters after seeing the price?',
    a: 'Yes. Mark those surfaces as optional lines in Scopie. The homeowner ticks them on the quote page, the total recalculates in front of them, and the choice is still there if they close the link and come back later.',
  },
  {
    q: 'What should my terms say about plants, paint and windows?',
    a: 'That you pre-wet and rinse plants, that failing or chalky paint can lift during washing, that old window seals may leak, and that you need an outside tap. Save those lines in your clause library so every quote carries them.',
  },
];

const page: IndustryPage = {
  slug: 'pressure-washing',
  order: 1,
  group: 'Outdoor',
  tile: 'pressure',
  path: '/for/pressure-washing',
  primaryKeyword: 'pressure washing business software',
  secondaryKeywords: ['pressure washing quote software', 'soft washing business software', 'pressure washing estimate app'],
  title: 'Pressure Washing Business Software for Quotes | Scopie', // 54
  metaDescription:
    'Quote house washes, driveways and patios by the square foot, add extras the homeowner can tick, and put your plant and paint clauses on every quote.', // 148
  h1: 'Pressure washing quotes, surface by surface',
  answerFirst:
    'Scopie lets pressure washing and soft washing companies quote each surface on its own: siding by area, flatwork by the square foot, roof and gutter work as extras. Homeowners add the patio or gutters with a tick, your plant and paint clauses ride along, and the signed job goes on the schedule with the right rig.',
  exampleQuote: { label: 'Sample exterior cleaning quote', lines, total: '$940', depositNote: 'Weather and water-access terms are the company\'s own clauses.' },
  blocks: [
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A full exterior, broken into surfaces',
      label: 'Sample quote: house wash, driveway and patio',
      lines,
      total: '$940',
      totalLabel: 'Total with the gutter brightening',
      note: 'Example figures. Gutter brightening is optional; the homeowner can tick it or leave it.',
    },
    {
      type: 'text',
      id: 'surfaces',
      h2: 'One price for "the outside" invites a cheaper flyer',
      lead: 'A neighbor got a door hanger offering the whole house for less. It did not include the patio.',
      paragraphs: [
        'Exterior cleaning is easy to underbid because homeowners cannot tell what is included. Listing siding, driveway, walks, patio, fence and roof separately, each with a size, shows the scope you are pricing and makes the cut-price offer look like what it is.',
        'It also gives the homeowner room to say yes to part of it. The driveway and house this spring, the fence and deck when the pollen is gone.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Quoting habits that cost exterior cleaners',
      style: 'mistakes',
      items: [
        { title: 'Method not stated', text: 'Soft wash or pressure? Say which surface gets which, or expect a worried call about the cedar shakes.' },
        { title: 'Plants and paint left to chance', text: 'Write how you protect beds and that oxidized or failing paint can come off.' },
        { title: 'Water source assumed', text: 'No outside tap, or a weak one, changes the day. Put it in the terms.' },
        { title: 'Rust and oil stains promised away', text: 'Some stains lighten but stay. Say so on the quote.' },
        { title: 'Roof work priced like siding', text: 'Roof soft washing is slower and riskier. Give it its own line and price.' },
      ],
    },
    {
      type: 'list',
      id: 'season',
      h2: 'The washing season',
      style: 'bullets',
      items: [
        { title: 'Spring', text: 'Green algae on the north side, pollen everywhere, and the busiest phone.' },
        { title: 'Summer', text: 'Decks, fences and patios before parties; HOA notices about mildew.' },
        { title: 'Fall', text: 'Pre-holiday house washes and driveway cleaning before sealing.' },
        { title: 'Winter', text: 'Freezing temperatures pause most residential work in colder regions; commercial flatwork carries on where it can.' },
      ],
    },
    {
      type: 'text',
      id: 'rig',
      h2: 'Signed tonight, on the trailer tomorrow',
      paragraphs: [
        'Text the quote link from the driveway; the homeowner reads it, ticks extras and signs. Once it is signed, put the job on the schedule with the crew and the rig it needs, such as the surface cleaner or the hot-water unit, so everybody can see which trailer is where. Crew members see the address and the time, while prices stay in the office.',
        'After the rinse, share the invoice link. The customer stays in your client list, so next spring\'s house wash is quoted from this year\'s sizes.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Pressure washing quote questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/cleaning', anchor: 'Scopie for cleaning businesses', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/templates/pressure-washing-quote-template', anchor: 'free pressure washing quote template', status: 'live' },
  ],
  cta: {
    heading: 'Beat the door hanger with a real quote',
    text: 'Save your surfaces, sizes and clauses in Scopie, and quote the next house before you roll up the hose.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
